import { useRef, useState } from "react";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import { Download, Search, Landmark } from "lucide-react";
import { api, fmtEth, shortAddr } from "../../lib/api";

export const StatementGenerator = () => {
  const [input, setInput] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const lookup = async () => {
    if (!/^0x[a-fA-F0-9]{40}$/.test(input.trim())) return toast.error("Enter a valid wallet address");
    setLoading(true);
    try {
      const r = await api.get(`/wallet/${input.trim()}`);
      if (r.data.configured === false) {
        toast.info("Statements go live once the $BANK contract is deployed");
        setData(null);
      } else setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Lookup failed");
    } finally {
      setLoading(false);
    }
  };

  const download = async () => {
    if (!ref.current) return;
    const canvas = await html2canvas(ref.current, { backgroundColor: "#0C0C0C", scale: 2 });
    const a = document.createElement("a");
    a.download = `hoodbank-statement-${shortAddr(data.address)}.png`;
    a.href = canvas.toDataURL("image/png");
    a.click();
  };

  return (
    <section id="statement" className="max-w-7xl mx-auto px-6 py-12">
      <p className="font-mono text-xs tracking-[0.2em] text-[#00C805] uppercase mb-3">Statement generator</p>
      <h2 className="font-heading font-bold text-lg tracking-tight mb-2">Your bank statement, on-chain</h2>
      <p className="text-sm text-neutral-500 mb-8 max-w-xl">Paste any wallet — read-only, no connection needed. This site never asks for keys or seed phrases.</p>
      <div className="grid lg:grid-cols-2 gap-10">
        <div>
          <div className="flex gap-3">
            <input
              data-testid="statement-address-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="0x…"
              className="flex-1 bg-[#050505] border border-white/10 rounded-full px-6 py-3.5 font-mono text-sm focus:outline-none focus:border-[#00C805]/60 transition-colors duration-200"
            />
            <button
              data-testid="statement-lookup-btn"
              onClick={lookup}
              disabled={loading}
              className="rounded-full bg-[#00C805] text-black font-semibold px-6 py-3 text-sm hover:bg-[#13D059] transition-colors duration-200 flex items-center gap-2 disabled:opacity-50"
            >
              <Search size={15} /> {loading ? "Reading…" : "Generate"}
            </button>
          </div>
          {data && (
            <button data-testid="statement-download-btn" onClick={download}
              className="mt-5 flex items-center gap-2 text-sm font-mono text-[#00C805] hover:text-[#13D059] transition-colors duration-200">
              <Download size={15} /> Download PNG
            </button>
          )}
        </div>
        <div>
          {data ? (
            <div ref={ref} data-testid="statement-card" className="rounded-2xl p-8 bg-[#0C0C0C] border border-[#00C805]/25">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <span className="w-7 h-7 rounded-full bg-[#00C805] flex items-center justify-center"><Landmark size={13} className="text-black" /></span>
                  <span className="font-heading font-bold">HoodBank Statement</span>
                </div>
                <span className="font-mono text-[10px] text-neutral-500">{new Date().toISOString().slice(0, 10)}</span>
              </div>
              <p className="font-mono text-xs text-neutral-400 break-all mb-6">{data.address}</p>
              <div className="grid grid-cols-2 gap-y-5 font-mono text-sm">
                <div><p className="text-[10px] tracking-[0.2em] text-neutral-500 uppercase">Balance</p><p className="mt-1">{fmtEth(data.balance_hood, 2)} BANK</p></div>
                <div><p className="text-[10px] tracking-[0.2em] text-neutral-500 uppercase">% of supply</p><p className="mt-1 text-[#00C805]">{data.percent_of_supply.toFixed(4)}%</p></div>
                <div><p className="text-[10px] tracking-[0.2em] text-neutral-500 uppercase">Lifetime ETH earned</p><p className="mt-1 text-[#00C805]">{fmtEth(data.lifetime_eth, 8)} ETH</p></div>
                <div><p className="text-[10px] tracking-[0.2em] text-neutral-500 uppercase">Unclaimed now</p><p className="mt-1">{fmtEth(data.unclaimed_eth, 8)} ETH</p></div>
                <div><p className="text-[10px] tracking-[0.2em] text-neutral-500 uppercase">Holder rank</p><p className="mt-1">{data.rank ? `#${data.rank}` : "—"}</p></div>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl p-8 card-surface h-full flex items-center justify-center min-h-[260px]">
              <p className="font-mono text-sm text-neutral-600">Statement preview appears here</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
